import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/ui/ui-button';
import { cardStyles, motionStyles, pageFrameStyles } from '../styles/lit-shared';

const CALENDAR_URL = 'https://calendar.app.google/3TMX3h7SGT1wkdp87';

const STEPS = [
  {
    title: 'El comercio tiene un soporte físico',
    body: 'Una pieza en mesa, escaparate o mostrador. Lleva NFC y QR. El cliente toca o escanea: no instala nada.',
  },
  {
    title: 'Llega a la URL activa',
    body: 'Ese tag no apunta a un destino grabado para siempre. Abre la URL que el establecimiento tenga asignada en ese momento.',
  },
  {
    title: 'El negocio cambia el destino cuando quiere',
    body: 'Oferta de temporada, carta, reseñas de Google, Instagram, una campaña concreta. Se cambia desde el panel, sin reimprimir el soporte.',
  },
  {
    title: 'Nosotros damos de alta la venta',
    body: 'El cobro va por Stripe. Al cerrar la venta, asignamos el Tag a ese usuario. El hardware y la cuenta quedan enlazados.',
  },
  {
    title: 'La operación corre por Telegram',
    body: 'El panel administrativo nuestro es un bot de Telegram que actúa como superusuario: registrar, asignar, corregir. Sin un backoffice pesado para el día a día.',
  },
] as const;

const FEATURES = [
  {
    title: 'NFC y QR en el mismo soporte',
    body: 'El cliente elige: tocar el tag o escanear el código. Dos caminos, una URL.',
  },
  {
    title: 'URL dinámica',
    body: 'Cambia el destino por temporada, oferta, temática o campaña. El plástico no se toca.',
  },
  {
    title: 'Panel del establecimiento',
    body: 'El negocio decide a dónde manda a la gente: reseñas, redes, carta, promo de esa semana.',
  },
  {
    title: 'Sin app para el cliente',
    body: 'Abre el navegador y listo. Cero fricción en mesa.',
  },
  {
    title: 'Altas con Stripe',
    body: 'La venta queda registrada. El Tag se asigna al usuario concreto que lo ha comprado.',
  },
  {
    title: 'Admin por Telegram',
    body: 'Un bot como superusuario para operar altas y asignaciones. Rápido de usar, difícil de perder.',
  },
] as const;

const USE_CASES = [
  {
    title: 'Ofertas de temporada',
    body: 'Navidad, verano, menú del día. Cambias la URL y el soporte físico sigue en su sitio.',
  },
  {
    title: 'Google Reseñas',
    body: 'Cuando te interesa captar opiniones, el tag apunta al perfil de reseñas. Cuando no, apunta a otra cosa.',
  },
  {
    title: 'Redes sociales',
    body: 'Instagram hoy, reserva mañana. El establecimiento no reimprime cada vez que cambia de prioridad.',
  },
  {
    title: 'Campaña concreta',
    body: 'Una landing, un sorteo, una carta PDF. El tag es el atajo; el destino lo eliges tú.',
  },
] as const;

const NOT_THIS = [
  'No es un QR estático impreso una vez y olvidado en la mesa.',
  'No obliga a rehacer el soporte cada vez que cambia la campaña.',
  'No es una app que el cliente tenga que descargar.',
  'No es un panel web pesado para operar nuestras altas: eso lo hace el bot de Telegram.',
] as const;

const COMPARE = [
  { without: 'QR grabado para siempre', with: 'URL que el negocio cambia cuando quiere' },
  { without: 'Reimprimir para cada campaña', with: 'El soporte físico se queda; cambia el destino' },
  { without: 'Una sola acción (reseña o red)', with: 'Temporadas, ofertas, reseñas o redes, según el momento' },
  { without: 'Alta manual y desordenada', with: 'Venta con Stripe y Tag asignado al usuario' },
  { without: 'Backoffice que nadie abre', with: 'Bot de Telegram como superusuario' },
] as const;

@customElement('tag-nfc-page')
export class TagNfcPage extends LitElement {
  static styles = [
    pageFrameStyles,
    cardStyles,
    motionStyles,
    css`
      .hero {
        position: relative;
        margin-bottom: var(--space-2xl);
        padding: var(--space-2xl) var(--space-xl);
        border-radius: var(--radius-lg);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        overflow: hidden;
        text-align: center;
      }

      .hero::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -10%;
        width: 300px;
        height: 300px;
        border-radius: 50%;
        background: var(--glow-cyan);
        filter: blur(50px);
        pointer-events: none;
      }

      .hero-content {
        position: relative;
        z-index: 1;
      }

      .eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        margin-bottom: var(--space-md);
        padding: 0.25rem 0.75rem;
        border-radius: var(--radius-full);
        background: rgb(255 255 255 / 6%);
        border: 1px solid var(--color-border);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-accent);
      }

      h1 {
        font-size: clamp(2rem, 5vw, 3rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin: 0 0 var(--space-md);
        color: var(--color-text);
      }

      .brand-name {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .tagline {
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--color-primary-light);
        margin: 0 0 var(--space-md);
      }

      .hero-lead {
        font-size: 1.125rem;
        color: var(--color-text-muted);
        max-width: 52ch;
        margin: 0 auto var(--space-xl);
        line-height: 1.6;
      }

      .hero-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-md);
        justify-content: center;
      }

      section {
        margin-bottom: var(--space-2xl);
      }

      .section-title {
        font-size: clamp(1.5rem, 3vw, 1.875rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        margin: 0 0 var(--space-sm);
      }

      .section-lead {
        color: var(--color-text-muted);
        font-size: 1rem;
        margin: 0 0 var(--space-xl);
        max-width: 56ch;
        line-height: 1.6;
      }

      .problem-list,
      .use-cases {
        display: grid;
        gap: var(--space-md);
        margin: 0;
        padding: 0;
        list-style: none;
      }

      @media (min-width: 700px) {
        .use-cases,
        .features {
          grid-template-columns: repeat(2, 1fr);
        }

        .compare {
          grid-template-columns: 1fr 1fr;
        }
      }

      .problem-list li,
      .use-case {
        padding: var(--space-lg);
      }

      .problem-list h3,
      .use-case h3,
      .feature h3 {
        margin: 0 0 var(--space-xs);
        font-size: 1.0625rem;
      }

      .problem-list p,
      .use-case p,
      .feature p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .flow-steps {
        display: grid;
        gap: var(--space-md);
      }

      .step {
        display: flex;
        gap: var(--space-md);
        align-items: flex-start;
      }

      .step-num {
        flex-shrink: 0;
        width: 2rem;
        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-full);
        background: var(--gradient-primary);
        color: var(--palette-white);
        font-weight: 700;
        font-size: 0.875rem;
      }

      .step h3 {
        margin: 0 0 0.25rem;
        font-size: 1rem;
      }

      .step p {
        margin: 0;
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .features {
        display: grid;
        gap: var(--space-md);
        grid-template-columns: 1fr;
      }

      .not-list {
        margin: 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: var(--space-sm);
      }

      .not-list li {
        padding: var(--space-md) var(--space-lg);
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        line-height: 1.55;
      }

      .compare {
        display: grid;
        gap: var(--space-md);
        grid-template-columns: 1fr;
      }

      .compare-col h3 {
        margin: 0 0 var(--space-md);
        font-size: 1rem;
      }

      .compare-col ul {
        margin: 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: var(--space-sm);
      }

      .compare-col li {
        font-size: 0.9375rem;
        color: var(--color-text-muted);
        line-height: 1.5;
        padding-left: 1rem;
        position: relative;
      }

      .compare-col--without li::before {
        content: '–';
        position: absolute;
        left: 0;
        color: var(--color-text-subtle);
      }

      .compare-col--with li::before {
        content: '+';
        position: absolute;
        left: 0;
        color: var(--color-accent);
        font-weight: 600;
      }

      .cta-block {
        margin-top: var(--space-2xl);
        padding: var(--space-2xl) var(--space-xl);
        border-radius: var(--radius-lg);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        text-align: center;
      }

      .cta-block h2 {
        margin: 0 0 var(--space-sm);
        font-size: clamp(1.375rem, 3vw, 1.75rem);
      }

      .cta-block p {
        margin: 0 auto var(--space-xl);
        max-width: 48ch;
        color: var(--color-text-muted);
        font-size: 1rem;
        line-height: 1.6;
      }
    `,
  ];

  render() {
    return html`
      <section class="hero animate-fade-up">
        <div class="hero-content">
          <span class="eyebrow">En desarrollo</span>
          <h1><span class="brand-name">NFC Pop</span></h1>
          <p class="tagline">Un soporte físico cuya URL no se queda grabada para siempre.</p>
          <p class="hero-lead">
            El cliente toca el NFC o escanea el QR y llega a la URL que el establecimiento haya
            elegido. Ofertas, temporadas, reseñas o redes: se cambia desde el panel, sin
            reimprimir el tag.
          </p>
          <div class="hero-actions">
            <ui-button href=${CALENDAR_URL} variant="primary">Hablar sobre NFC Pop</ui-button>
          </div>
        </div>
      </section>

      <section>
        <h2 class="section-title">El problema que resuelve</h2>
        <p class="section-lead">
          Un QR en la mesa suele morir en la primera campaña. NFC Pop separa el objeto físico
          del destino digital.
        </p>
        <ul class="problem-list">
          <li class="card">
            <h3>El plástico no perdona el cambio de idea</h3>
            <p>
              Imprimes un QR a Google Reseñas y a los dos meses quieres mandar a la carta de
              verano. Toca reimprimir, o convivir con un destino que ya no sirve.
            </p>
          </li>
          <li class="card">
            <h3>Cada temporada pide un enlace distinto</h3>
            <p>
              Ofertas, temáticas, redes, reseñas. El comercio necesita un atajo físico estable
              y un destino que pueda mover.
            </p>
          </li>
          <li class="card">
            <h3>Operar altas no debería ser un ERP</h3>
            <p>
              Vendemos el tag, cobramos con Stripe y lo asignamos al usuario. El día a día
              administrativo cabe en un bot de Telegram.
            </p>
          </li>
        </ul>
      </section>

      <section>
        <h2 class="section-title">Cómo funciona</h2>
        <p class="section-lead">Hardware en el local. URL en el panel. Operación por Telegram.</p>
        <div class="flow-steps">
          ${STEPS.map(
            (step, i) => html`
              <div class="step">
                <span class="step-num">${i + 1}</span>
                <div>
                  <h3>${step.title}</h3>
                  <p>${step.body}</p>
                </div>
              </div>
            `,
          )}
        </div>
      </section>

      <section>
        <h2 class="section-title">Para qué lo usa el comercio</h2>
        <p class="section-lead">Un solo tag. Varios destinos, según el momento.</p>
        <div class="use-cases">
          ${USE_CASES.map(
            (item) => html`
              <article class="card use-case">
                <h3>${item.title}</h3>
                <p>${item.body}</p>
              </article>
            `,
          )}
        </div>
      </section>

      <section>
        <h2 class="section-title">Qué incluye</h2>
        <p class="section-lead">La pieza física, el destino cambiable y la operación de venta.</p>
        <div class="features">
          ${FEATURES.map(
            (f) => html`
              <article class="card feature">
                <h3>${f.title}</h3>
                <p>${f.body}</p>
              </article>
            `,
          )}
        </div>
      </section>

      <section>
        <h2 class="section-title">Qué no es NFC Pop</h2>
        <p class="section-lead">Para evitar expectativas equivocadas. El producto está en desarrollo.</p>
        <ul class="not-list">
          ${NOT_THIS.map((item) => html`<li class="card">${item}</li>`)}
        </ul>
      </section>

      <section>
        <h2 class="section-title">Sin NFC Pop vs con NFC Pop</h2>
        <div class="compare">
          <div class="card compare-col compare-col--without">
            <h3>Sin NFC Pop</h3>
            <ul>
              ${COMPARE.map((row) => html`<li>${row.without}</li>`)}
            </ul>
          </div>
          <div class="card compare-col compare-col--with">
            <h3>Con NFC Pop</h3>
            <ul>
              ${COMPARE.map((row) => html`<li>${row.with}</li>`)}
            </ul>
          </div>
        </div>
      </section>

      <div class="cta-block animate-fade-up">
        <h2>En desarrollo</h2>
        <p>
          Estamos montando el soporte, el panel y la operación por Telegram. Si tienes un
          local o una cadena y esto te encaja, hablemos mientras lo cerramos.
        </p>
        <ui-button href=${CALENDAR_URL} variant="primary">Hablar sobre NFC Pop</ui-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tag-nfc-page': TagNfcPage;
  }
}
